/**
 * A single-flight queue with a trailing re-run.
 *
 * THE DEFECT IT EXISTS TO FIX is in the vanilla `scheduleServerSync()`: it debounces by
 * 800 ms and then calls `syncToServer()` with NO IN-FLIGHT GUARD. A slow `PUT /api/map` and
 * a second edit 900 ms later overlap, and two full structural resyncs are in the air at once.
 * That is not a theoretical race — the payload always resends every node's current values, and
 * the server answers each PUT with a new `dataVersion` which the client stores. Whichever
 * response lands last wins the version, so the version the client believes it has can be the
 * one from the EARLIER request; the next PUT then carries a stale `dataVersion` and comes back
 * `409`, which the app handles by telling the user the map changed elsewhere and reloading.
 * REACT-REWRITE-PLAN.md §"Stage 3" names the fix: "a single-flight queue with a trailing
 * re-run."
 *
 * WHY THE BODY IS BUILT AT FLUSH TIME rather than passed in. `request()` takes no payload; the
 * queue calls the `run` function it was created with, and that function reads current state
 * when it runs. If the body were captured at request time, the trailing run would send the
 * state as it was when the LAST overlapping request came in — which is stale by definition,
 * because more edits are what caused the overlap. The whole point of coalescing is that one
 * later, complete send replaces several earlier partial ones.
 *
 * WHAT THIS IS NOT. It is not a debounce, and it should not grow into one. The 800 ms
 * quiet-period is a separate decision about when an edit is finished being made; this is about
 * never having two of the same request in flight. Composing them is the caller's job — and
 * keeping them apart is what lets the map commit immediately on `pointerup` while still
 * debouncing keystroke-driven edits.
 */

export interface SingleFlightQueue {
  /**
   * Ask for a run.
   *
   * Resolves when a run that started at or after this call has settled — so awaiting it means
   * "my state has been sent", not merely "a request happened". A call made while a run is in
   * flight does not start a second one; it joins the trailing run, and any number of calls
   * during one flight collapse into exactly one trailing run.
   */
  request: () => Promise<void>;
  /** True while a run is in flight. For tests and for a "syncing…" indicator. */
  readonly inFlight: boolean;
  /** True when a trailing run is owed. */
  readonly pending: boolean;
}

export function createSingleFlightQueue(run: () => Promise<void>): SingleFlightQueue {
  let inFlight = false;
  let pending = false;
  /** Resolvers for callers waiting on a run that has not started yet. */
  let waiting: { resolve: () => void; reject: (e: unknown) => void }[] = [];

  const start = (): void => {
    inFlight = true;
    pending = false;
    const mine = waiting;
    waiting = [];

    // `run` is called inside a try because a function that throws SYNCHRONOUSLY would
    // otherwise escape past the settle handling below and leave `inFlight` true for ever —
    // the queue wedged shut, which is a worse failure than the one it was built to fix.
    let flight: Promise<void>;
    try {
      flight = run();
    } catch (err) {
      flight = Promise.reject(err);
    }

    const settle = (deliver: (w: (typeof mine)[number]) => void): void => {
      inFlight = false;
      for (const w of mine) deliver(w);
      // A failure must not wedge the queue, and neither must a success. The trailing run
      // starts AFTER this flight's waiters have settled, so a caller awaiting this one is
      // never held open by work it did not ask for.
      if (pending) start();
    };

    void flight.then(
      () => settle((w) => w.resolve()),
      (err: unknown) => settle((w) => w.reject(err)),
    );
  };

  return {
    request: () =>
      new Promise<void>((resolve, reject) => {
        waiting.push({ resolve, reject });
        if (inFlight) {
          pending = true;
          return;
        }
        start();
      }),
    get inFlight() {
      return inFlight;
    },
    get pending() {
      return pending;
    },
  };
}
